import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";

const faqs = [
  {
    q: "What makes Corrective Motion different from a regular personal trainer?",
    a: "Every program starts with a clinical movement assessment. The system is rooted in biomechanics, motor control, and evidence-based corrective exercise, not generic gym workouts. Strength, fat loss, and aesthetics are built on top of a foundation of pain-free, joint-friendly movement.",
  },
  {
    q: "Do I need to be injured or in pain to train here?",
    a: "Not at all. Clients range from elite professionals chasing PRs, to people recovering from years of desk-bound posture, to first-time lifters. The same principles apply: assess, correct, and build.",
  },
  {
    q: "How long until I see results?",
    a: "Most clients report meaningful change in mobility, posture, and pain within 4 to 6 weeks. Body composition and strength gains compound over 8 to 16 weeks with consistency. We measure progress objectively at every milestone.",
  },
  {
    q: "Where exactly do you train clients?",
    a: "In-person sessions are conducted at Train Strength & Fitness inside Dubai Mall · Zabeel Expansion. Online coaching is also available for clients outside Dubai.",
  },
  {
    q: "How is pricing handled?",
    a: "Pricing is consultation-based. After your initial assessment, you'll receive a tailored proposal aligned with your goals, frequency, and format (in-person or online).",
  },
  {
    q: "Is corrective exercise suitable for older clients or those with chronic pain?",
    a: "Yes. That's exactly who the system is built for. Corrective Motion is suitable for all ages and levels, and is especially effective for chronic pain, posture issues, and joint health.",
  },
];

export const FAQ = () => {
  const [open, setOpen] = useState(0);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-20 sm:py-28 lg:py-36 border-t border-white/5"
    >
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-14">
          <div className="text-xs uppercase tracking-[0.3em] text-[#00E5FF] mb-4">Questions, Answered</div>
          <h2 className="font-display text-4xl lg:text-5xl xl:text-6xl font-extrabold leading-[1.05] tracking-[-0.02em] text-white">
            Everything you'd <span className="text-gradient-blue">want to ask.</span>
          </h2>
        </div>

        <div className="space-y-3">
          {faqs.map((f, i) => {
            const isOpen = open === i;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                data-testid={`faq-item-${i}`}
                className={`rounded-2xl border transition-colors ${
                  isOpen ? "bg-white/[0.04] border-[#00E5FF]/30" : "bg-white/[0.02] border-white/10"
                }`}
              >
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full flex items-start justify-between gap-6 text-left p-6"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-display text-lg sm:text-xl font-semibold text-white pr-4">
                    {f.q}
                  </span>
                  <span
                    className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center border border-white/10 transition-transform ${
                      isOpen ? "rotate-45 bg-gradient-to-br from-[#0055FF] to-[#00E5FF] border-transparent" : "bg-black/30"
                    }`}
                  >
                    <Plus className={`w-4 h-4 ${isOpen ? "text-black" : "text-white"}`} />
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 text-zinc-300/90 leading-relaxed">
                        {f.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
